import { useEffect, useState } from 'react';
import {
  Button, DeleteButton, Identifier, useRecordContext, useTranslate,
} from 'react-admin';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { ObservationTrigger } from '@x-guard/xgac-types/xgac';
import _ from 'lodash';
import DeleteIcon from '@mui/icons-material/Delete';
import authProvider from '../../utils/authProvider';
import { getCurrentCustomer } from '../../lib/currentCustomer';
import { xgacDataProvider } from '../../dataProviders/xgacDataProvider';
import { atsDataProvider } from '../../dataProviders/atsDataProvider';
import { KioskUser } from '../../lib/constants/customTypes';

type LinksTriggersKiosksCache = {
  customerId: string;
  links: any[];
  triggers: ObservationTrigger[];
  kiosks: KioskUser[];
};

let linksTriggersKiosksCache: LinksTriggersKiosksCache | null = null;
let linksTriggersKiosksInflight: {
  customerId: string;
  promise: Promise<LinksTriggersKiosksCache>;
} | null = null;

const loadLinksTriggersAndKiosks = async (
  customerId: string,
  options?: { force?: boolean },
): Promise<LinksTriggersKiosksCache> => {

  if (
    !options?.force
    && linksTriggersKiosksCache?.customerId === customerId
  ) {

    return linksTriggersKiosksCache;

  }

  if (linksTriggersKiosksInflight?.customerId === customerId) {

    return linksTriggersKiosksInflight.promise;

  }

  const promise = (async (): Promise<LinksTriggersKiosksCache> => {

    try {

      const [triggerRequest, linkRequest, kioskRequest] = await Promise.all([

        xgacDataProvider.getList('observation-triggers', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        }),

        atsDataProvider.getList('customer-links', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: {},
        }),

        xgacDataProvider.getList('specials/kiosk-user', {
          pagination: { page: 1, perPage: -1 },
          sort: { field: 'createdAt', order: 'ASC' },
          filter: {},
        }),
      ]);

      const next: LinksTriggersKiosksCache = {
        customerId,
        links: linkRequest.data,
        triggers: triggerRequest.data,
        kiosks: kioskRequest.data,
      };
      linksTriggersKiosksCache = next;
      return next;

    } finally {

      if (linksTriggersKiosksInflight?.customerId === customerId) {

        linksTriggersKiosksInflight = null;

      }

    }

  })();

  linksTriggersKiosksInflight = { customerId, promise };
  return promise;

};

export const useLinksTriggersAndKiosks = () => {

  const currentCustomer = getCurrentCustomer();
  const customerId = currentCustomer?.value;

  const cached = customerId && linksTriggersKiosksCache?.customerId === customerId ? linksTriggersKiosksCache : null;

  const [links, setLinks] = useState<any[]>(cached?.links ?? []);
  const [triggers, setTriggers] = useState<ObservationTrigger[]>(cached?.triggers ?? []);
  const [kiosks, setKiosks] = useState<KioskUser[]>(cached?.kiosks ?? []);
  const [isLoading, setIsLoading] = useState(!cached);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {

    let cancelled = false;

    if (!customerId) {

      return () => {

        cancelled = true;

      };

    }

    const hasValidCache = linksTriggersKiosksCache?.customerId === customerId;

    if (!hasValidCache) {

      setIsLoading(true);
      setLoadFailed(false);

    }

    loadLinksTriggersAndKiosks(customerId, { force: !hasValidCache })
      .then((data) => {

        if (cancelled) return;
        if (data.customerId !== customerId) return;
        setLinks(data.links);
        setTriggers(data.triggers);
        setKiosks(data.kiosks);
        setLoadFailed(false);
        setIsLoading(false);

      })
      .catch(() => {

        if (cancelled) return;

        if (!hasValidCache) {

          setLoadFailed(true);

        }

        setIsLoading(false);

      });

    return () => {

      cancelled = true;

    };

  }, [customerId]);

  return {
    links, triggers, kiosks, isLoading, loadFailed,
  };

};

export const useIsUsedInLinkTriggerOrKiosk = (ids: Identifier[]) => {

  const {
    links, triggers, kiosks, isLoading, loadFailed,
  } = useLinksTriggersAndKiosks();
  const [isUsed, setIsUsed] = useState(true);

  useEffect(() => {

    if (isLoading || loadFailed) {

      setIsUsed(true);
      return;

    }

    if (!ids) return;

    let tempIsUsed = false;
    for (const id of ids) {

      const linksAndTriggers = [...links, ...triggers];
      const isUsedInLinkOrTrigger = linksAndTriggers.some((linkOrTrigger) => {

        return JSON.stringify(linkOrTrigger).includes(id as string);

      });
      const isUsedInKiosk = kiosks.some((kiosk) => {

        const kioskConfigFields = ['exposeAssets', 'exposeLabels'];

        for (const field of kioskConfigFields) {

          const fieldValue = _.get(kiosk.kioskConfig, field);

          if (Array.isArray(fieldValue)) {

            for (const value of fieldValue) {

              if (value._id === id) {

                return true;

              }

            }

          }

        }
        return false;

      });

      tempIsUsed = tempIsUsed || isUsedInLinkOrTrigger || isUsedInKiosk;
      if (tempIsUsed) break;

    }
    setIsUsed(tempIsUsed);

  }, [links, triggers, ids, kiosks, isLoading, loadFailed]);

  return isUsed;

};

export const AssetGroupDeleteButton = (props: {
  className?: string;
}) => {

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const translate = useTranslate();
  const record = useRecordContext();
  const isUsed = useIsUsedInLinkTriggerOrKiosk([record?.id || '']);
  const isAdmin = authProvider.isAdmin();
  const customMessage = `${translate('ra.action.delete')} ${record?.name}`;
  const closeDialog = () => {

    setDialogOpen(false);
    setDeleteEnabled(false);

  };

  const bhvkDestructiveButtonProps = props.className ? {
    className: props.className,
    variant: 'text' as const,
    size: 'small' as const,
  } : {};

  const bhvkDestructiveDeleteProps = props.className
    ? {
      className: props.className,
      variant: 'text' as const,
      size: 'small' as const,
      color: 'inherit' as const,
      icon: <></>,
    }
    : {};

  if (isAdmin) {

    return (
      <>
        {isUsed ? (
          <>
            <Button
              onClick={() => setDialogOpen(true)}
              label="ra.action.delete"
              color={props.className ? 'inherit' : 'error'}
              startIcon={props.className ? undefined : <DeleteIcon />}
              {...bhvkDestructiveButtonProps}
            />
            <Dialog open={dialogOpen} onClose={closeDialog}>
              <DialogTitle>{customMessage}</DialogTitle>
              <DialogContent>
                <div>
                  <Typography variant={'body1'}>{translate(
                    'ra.message.delete_content',
                  )}</Typography>
                  <br/>
                  <Typography
                    fontWeight="fontWeightBold"
                    variant={'body1'}
                    color="error">
                    {translate('resources.asset-groups.text.delete_warning')}
                  </Typography>
                  <br/>
                </div>
                <FormControlLabel control={
                  <Checkbox checked={deleteEnabled} onChange={(event, checked) => setDeleteEnabled(checked)}/>
                } label={translate('resources.asset-groups.text.delete_confirmation')}/>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={closeDialog}
                  label={translate('ra.action.cancel')}
                />
                <DeleteButton
                  disabled={!deleteEnabled}
                />
              </DialogActions>
            </Dialog>
          </>
        ) : (
          <DeleteButton
            confirmTitle={customMessage}
            mutationMode="pessimistic"
            {...bhvkDestructiveDeleteProps}
          />
        )}
      </>
    );

  }
  if (isUsed) {

    return (
      <Tooltip title={translate('resources.asset-groups.text.delete_warning')}>
        <div>
          <DeleteButton
            mutationMode="pessimistic"
            disabled={isUsed}
            confirmTitle={customMessage}
            {...bhvkDestructiveDeleteProps}
          />
        </div>
      </Tooltip>
    );

  }

  return (
    <DeleteButton
      disabled={isUsed}
      mutationMode="pessimistic"
      confirmTitle={customMessage}
      {...bhvkDestructiveDeleteProps}
    />
  );

};
